import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const ExpertiseShowcase = () => {
  const [activeCase, setActiveCase] = useState(0);

  const caseStudies = [
    {
      title: 'Data Strategy Transformation',
      industry: 'Financial Services',
      challenge: 'Mid-cap financial services firm struggling with fragmented data systems, compliance risks, and inability to leverage data for competitive advantage.',
      solution: 'Developed comprehensive 3-year data strategy, implemented governance framework, and established CDO-level oversight for transformation initiatives.',
      outcomes: [
        { metric: 'Compliance Score', value: '100%', description: 'Full GDPR and regulatory compliance' },
        { metric: 'Data Quality', value: '85%', description: 'Improvement in data accuracy' },
        { metric: 'Time to Insight', value: '70%', description: 'Reduction in reporting cycles' }
      ],
      icon: 'Target'
    },
    {
      title: 'Board-Level Presentation Excellence',
      industry: 'Healthcare Technology',
      challenge: 'Healthcare tech company needed to communicate complex data initiatives to non-technical board members and secure investment approval.',
      solution: 'Created executive-level data strategy presentations, ROI models, and risk assessments that translated technical complexity into business value.',
      outcomes: [
        { metric: 'Board Approval', value: '€5M', description: 'Data transformation budget secured' },
        { metric: 'Stakeholder Confidence', value: '95%', description: 'Board satisfaction rating' },
        { metric: 'Strategic Clarity', value: '100%', description: 'Alignment on data priorities' }
      ],
      icon: 'Presentation'
    },
    {
      title: 'Organizational Change Management',
      industry: 'Retail',
      challenge: 'Retail organization facing resistance to data-driven culture change and struggling with team capability gaps during digital transformation.',
      solution: 'Led change management program, developed internal data champions, and implemented mentoring framework to elevate team capabilities.',
      outcomes: [
        { metric: 'Team Capability', value: '200%', description: 'Increase in data literacy' },
        { metric: 'Adoption Rate', value: '90%', description: 'Of new data tools and processes' },
        { metric: 'Employee Satisfaction', value: '4.5/5', description: 'With data transformation' }
      ],
      icon: 'Users'
    }
  ];

  const currentCase = caseStudies?.[activeCase];

  return (
    <div>
      <div className="text-center mb-12">
        <h2 className="text-3xl lg:text-4xl font-bold mb-4">Strategic Expertise in Action</h2>
        <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
          Real-world examples of CDO-level strategic thinking and transformation leadership
        </p>
      </div>
      <div className="flex justify-center mb-8">
        <div className="inline-flex bg-surface rounded-lg p-1">
          {caseStudies?.map((study, index) => (
            <button
              key={index}
              onClick={() => setActiveCase(index)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
                activeCase === index
                  ? 'bg-white text-primary shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {study?.title}
            </button>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-elevation p-8 border border-border">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name={currentCase?.icon} size={24} className="text-primary" />
              </div>
              <div>
                <h3 className="text-2xl font-bold">{currentCase?.title}</h3>
                <div className="text-sm text-muted-foreground">{currentCase?.industry}</div>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <h4 className="font-semibold mb-2 flex items-center space-x-2">
                  <Icon name="AlertCircle" size={16} className="text-secondary" />
                  <span>Challenge</span>
                </h4>
                <p className="text-muted-foreground">{currentCase?.challenge}</p>
              </div>

              <div>
                <h4 className="font-semibold mb-2 flex items-center space-x-2">
                  <Icon name="Lightbulb" size={16} className="text-primary" />
                  <span>Strategic Solution</span>
                </h4>
                <p className="text-muted-foreground">{currentCase?.solution}</p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Measurable Outcomes</h4>
            <div className="space-y-4">
              {currentCase?.outcomes?.map((outcome, index) => (
                <div key={index} className="p-4 bg-surface rounded-lg border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-muted-foreground">{outcome?.metric}</span>
                    <span className="text-2xl font-bold text-primary">{outcome?.value}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{outcome?.description}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-primary/5 rounded-lg">
              <div className="flex items-start space-x-2">
                <Icon name="Award" size={20} className="text-primary mt-1" />
                <div>
                  <div className="font-semibold mb-1">Executive Impact</div>
                  <p className="text-sm text-muted-foreground">
                    Board-level strategic thinking combined with hands-on transformation leadership to deliver measurable business value.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpertiseShowcase;